<script setup lang="ts">
import { shallowRef, useTemplateRef } from 'vue'
import AppPageCarousel from '@/components/app-page-carousel'
import AppPageMask from '@/components/app-page-mask'
import AppPageOpenButton from '@/components/app-page-open-button'
import { useAppLaunch } from '@/composables/useAppLaunch'
import { useAppPageCarousel } from '@/composables/useAppPageCarousel'
import { useAppPageScale } from '@/composables/useAppPageScale'
import { APP_PAGE_ASSETS, APP_PAGE_SLIDES } from '@/constants/app'
import type { Props } from './type'

defineOptions({
  name: 'AppPage',
})

defineProps<Props>()

const openButtonAnchor = useTemplateRef<HTMLElement>('openButtonAnchor')
const overlayBlocked = shallowRef(false)
const { scale, stageStyle } = useAppPageScale()
const {
  activeIndex,
  progress,
  slidesStyle,
  handleDotClick,
  handleTouchStart,
  handleTouchMove,
  handleTouchEnd,
  handleTouchCancel,
  markUserInteraction,
  pauseForOverlay,
  resumeAfterOverlay,
} = useAppPageCarousel({
  slideCount: APP_PAGE_SLIDES.length,
  getScale: () => scale.value,
  getSwipeBoundaryTop: () =>
    openButtonAnchor.value?.getBoundingClientRect().top ?? Number.POSITIVE_INFINITY,
  isOverlayOpen: () => overlayBlocked.value,
})
const { activeMask, toastMessage, hideMask, tryOpenApp } = useAppLaunch({
  onShowOverlay: () => {
    overlayBlocked.value = true
    pauseForOverlay()
  },
  onHideOverlay: () => {
    overlayBlocked.value = false
    resumeAfterOverlay()
  },
})

function handleOpenClick() {
  markUserInteraction()
  tryOpenApp()
}
</script>

<template>
  <div class="app-page">
    <div
      class="app-page__stage"
      :style="stageStyle"
      @touchstart="handleTouchStart"
      @touchmove="handleTouchMove"
      @touchend="handleTouchEnd"
      @touchcancel="handleTouchCancel"
    >
      <header class="app-page__hero">
        <h1 class="app-page__title">
          来造梦<br />
          造一百种世界
        </h1>
      </header>

      <AppPageCarousel
        :slides="APP_PAGE_SLIDES"
        :active-index="activeIndex"
        :progress="progress"
        :slides-style="slidesStyle"
        @dot-click="handleDotClick"
      />

      <div ref="openButtonAnchor" class="app-page__open-button-anchor">
        <AppPageOpenButton
          :icon="APP_PAGE_ASSETS.appIcon"
          label="打开造梦次元App"
          @click="handleOpenClick"
        />
      </div>
    </div>

    <AppPageMask
      kind="wechat"
      :open="activeMask === 'wechat'"
      :guide-image="APP_PAGE_ASSETS.openInBrowserGuide"
      :qr-image="APP_PAGE_ASSETS.appDownloadQr"
      @close="hideMask"
    />
    <AppPageMask
      kind="qr"
      :open="activeMask === 'qr'"
      :guide-image="APP_PAGE_ASSETS.openInBrowserGuide"
      :qr-image="APP_PAGE_ASSETS.appDownloadQr"
      @close="hideMask"
    />

    <div v-if="toastMessage" class="app-page__toast" role="status">{{ toastMessage }}</div>
  </div>
</template>

<style scoped>
.app-page {
  --app-page-brand: #06c755;
  --app-page-text: #171717;
  --app-page-subtext: #b6b6b6;
  --app-page-dot: rgb(0 0 0 / 0.08);

  position: fixed;
  inset: 0;
  overflow: hidden;
  color: var(--app-page-text);
  font-family: 'PingFang SC', 'Helvetica Neue', 'Noto Sans SC', var(--sys-sans);
  background: #fff;
}

.app-page__stage {
  position: absolute;
  top: 50%;
  left: 50%;
  width: 400px;
  height: 800px;
  overflow: hidden;
  touch-action: pan-y;
  background: #fff;
  transform-origin: center center;
}

.app-page__hero {
  position: absolute;
  top: 94px;
  left: 0;
  width: 400px;
}

.app-page__title {
  margin: 0;
  color: var(--app-page-brand);
  font-size: 37px;
  font-weight: 600;
  letter-spacing: 0.08em;
  line-height: 1.2578;
  text-align: center;
  white-space: nowrap;
}

.app-page__open-button-anchor {
  position: absolute;
  top: 675px;
  left: 57px;
  width: 286px;
  height: 60px;
}

.app-page__toast {
  position: fixed;
  bottom: calc(90px + env(safe-area-inset-bottom));
  left: 50%;
  z-index: 1001;
  max-width: min(320px, calc(100vw - 32px));
  padding: 9px 12px;
  color: #fff;
  font-size: 13px;
  line-height: 1.35;
  text-align: center;
  background: rgb(20 20 20 / 0.88);
  border-radius: 10px;
  transform: translateX(-50%);
}
</style>
